import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOCPP } from '../contexts/OCPPContext';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const location = useLocation();
  const { connectionStatus, stats } = useOCPP();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <header className="App-header">
      <div className="container">
        <nav className="nav">
          <Link to="/" className="nav-brand">
            OCPP Server MEDA
          </Link>
          
          <ul className="nav-links">
            <li>
              <Link 
                to="/" 
                className={isActive('/') ? 'active' : ''}
              >
                Dashboard
              </Link>
            </li>
            <li>
              <Link 
                to="/emergency" 
                className={isActive('/emergency') ? 'active' : ''}
              >
                Emergency Control
              </Link>
            </li>
            <li>
              <Link
                to="/history"
                className={isActive('/history') ? 'active' : ''}
              >
                History
              </Link>
            </li>
            {user?.role === 'admin' && (
              <li>
                <Link
                  to="/connection-log"
                  className={isActive('/connection-log') ? 'active' : ''}
                >
                  Connection Log
                </Link>
              </li>
            )}
            {user?.role === 'admin' && (
              <li>
                <Link
                  to="/ocpp-trace"
                  className={isActive('/ocpp-trace') ? 'active' : ''}
                >
                  OCPP Trace
                </Link>
              </li>
            )}
            {user?.role === 'admin' && (
              <li>
                <Link
                  to="/sites"
                  className={isActive('/sites') ? 'active' : ''}
                >
                  Sites
                </Link>
              </li>
            )}
            {user?.role === 'admin' && (
              <li>
                <Link
                  to="/fleet"
                  className={isActive('/fleet') ? 'active' : ''}
                >
                  Fleet
                </Link>
              </li>
            )}
            {user?.role === 'admin' && (
              <li>
                <Link
                  to="/users"
                  className={isActive('/users') ? 'active' : ''}
                >
                  User Management
                </Link>
              </li>
            )}
            {user?.role === 'admin' && (
              <li>
                <Link
                  to="/simulator"
                  className={isActive('/simulator') ? 'active' : ''}
                  style={{ color: '#ffc107' }}
                >
                  Simulator
                </Link>
              </li>
            )}
          </ul>
          
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="status-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span 
                  className={`status-indicator ${
                    connectionStatus === 'connected' ? 'status-connected' : 'status-disconnected'
                  }`}
                />
                <span style={{ fontSize: '0.9rem' }}>
                  {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                {stats.connectedChargers}/{stats.totalChargers} Chargers Online
              </div>
            </div>

            {/* User menu */}
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: user?.role === 'admin' ? '#28a745' : '#007bff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span>{user?.username}</span>
                <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                  ({user?.role})
                </span>
                <span style={{ fontSize: '0.7rem' }}>▼</span>
              </button>

              {showUserMenu && (
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  minWidth: '200px',
                  marginTop: '0.5rem'
                }}>
                  <div style={{ padding: '1rem', borderBottom: '1px solid #eee' }}>
                    <div style={{ fontWeight: 'bold', color: '#333' }}>{user?.username}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{user?.email}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      Role: <span style={{ 
                        color: user?.role === 'admin' ? '#28a745' : '#007bff',
                        fontWeight: 'bold'
                      }}>
                        {user?.role}
                      </span>
                    </div>
                  </div>
                  
                  {user?.role === 'admin' && (
                    <div style={{ borderBottom: '1px solid #eee' }}>
                      <Link 
                        to="/admin" 
                        style={{
                          display: 'block',
                          padding: '0.75rem 1rem',
                          color: '#333',
                          textDecoration: 'none'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowUserMenu(false)}
                      >
                        User Management
                      </Link>
                    </div>
                  )}
                  
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      color: '#dc3545',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;